/**
 * EcommerceHeader - Responsive header: compact on mobile, full on desktop
 * Style « marché artisanal » : fond crème, liseré terracotta, wordmark Domine
 * Mobile: logo + search icon + wallet mini badge
 * Desktop: logo + search bar + liens + user menu
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useCustomer } from '../../../contexts/CustomerContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useNewPublicationsCount } from '../../../hooks/useNewPublicationsCount';
import { ecommerceApi } from '../../../api/ecommerce.api';
import { logger } from '../../../utils/logger';

// Palette boutique (design « marché terracotta/olive »)
const TERRACOTTA = '#b45f2e';
const OLIVE = '#4a5c3a';

interface EcommerceHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export default function EcommerceHeader({ searchQuery, onSearchChange }: EcommerceHeaderProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { customer, isAuthenticated, logout } = useCustomer();
  const { tenant } = useTenant();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const actusNonLues = useNewPublicationsCount();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Charger le solde du wallet
  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const tenantId = localStorage.getItem('customer_tenant_id');

    if (isAuthenticated && customer && token && tenantId) {
      loadWalletBalance();
      const interval = setInterval(loadWalletBalance, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, customer]);

  // Auto-focus search input when expanded on mobile
  useEffect(() => {
    if (showMobileSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showMobileSearch]);

  const loadWalletBalance = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;

    try {
      const walletData = await ecommerceApi.wallet.getBalance();
      setWalletBalance(walletData.balance_cents);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        logger.error('Erreur chargement solde wallet:', error);
      }
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/shop');
  };

  const ecommerceConfig = tenant?.config?.ecommerce;
  /**
   * Le logo ramène à l'accueil de la boutique — et quand on Y EST DÉJÀ, il la
   * remet à zéro : rayon, filtres, recherche.
   *
   * `navigate('/shop')` depuis `/shop` ne remonte pas la page : le rayon
   * sélectionné et les filtres restaient en place, donc cliquer le logo ne
   * faisait rien de visible (signalé le 10/09/2026). L'horodatage dans l'état de
   * navigation est ce que le catalogue observe pour se réinitialiser : deux
   * clics de suite produisent deux valeurs différentes, donc deux remises à zéro.
   */
  const retourAccueil = () => navigate('/shop', { state: { accueil: Date.now() } });

  const storeName = ecommerceConfig?.storeName || tenant?.name || 'FOLIES BOUTIK';
  const logoSrc = ecommerceConfig?.logo || '/logo-folies.png';

  // Mobile header
  if (isMobile) {
    return (
      <header className="shop-sans bg-[#faf7f0] border-b-2 border-[#b45f2e]">
        {/* Mobile search overlay */}
        {showMobileSearch ? (
          <div className="flex items-center gap-2 px-3 py-2.5">
            <div className="flex-1 relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Rechercher un produit bio…"
                className="w-full pl-4 pr-10 py-2.5 text-sm bg-white border border-black/[.12] rounded-lg placeholder-[#6b6550] text-[#3a3324] focus:outline-none focus:border-[#b45f2e]"
              />
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-[#f0e9d8] rounded-full"
                >
                  <X className="w-3.5 h-3.5 text-[#4a4530]" />
                </button>
              )}
            </div>
            <button
              onClick={() => {
                setShowMobileSearch(false);
                if (!searchQuery) onSearchChange('');
              }}
              className="text-sm font-semibold text-[#4a5c3a] px-2 py-2"
            >
              Fermer
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between px-3 py-2.5">
            {/* Logo + Name (compact) */}
            <button
              onClick={() => retourAccueil()}
              className="flex items-center gap-2"
              aria-label="Retour à l'accueil de la boutique"
            >
              <img src={logoSrc} alt={storeName} className="h-8 w-8 rounded-full object-contain" />
              <span className="shop-serif font-bold text-base truncate max-w-[160px]" style={{ color: TERRACOTTA }}>
                {storeName}
              </span>
            </button>

            {/* Right actions */}
            <div className="flex items-center gap-1">
              {/* Search button */}
              {/* Une icône seule n'a pas de nom : un lecteur d'écran annonçait
                  « bouton » et rien d'autre (audit du 15/09/2026). */}
              <button
                onClick={() => setShowMobileSearch(true)}
                aria-label="Rechercher un produit"
                className="w-10 h-10 flex items-center justify-center rounded-full text-[#4a5c3a] active:bg-[#f0e9d8]"
              >
                <Search className="w-5 h-5" aria-hidden="true" />
              </button>

              {/* Wallet mini badge (authenticated only) */}
              {isAuthenticated && (
                <button
                  onClick={() => navigate('/shop/wallet')}
                  className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#f0e9d8] border border-[#4a5c3a]/30"
                  style={{ color: OLIVE }}
                >
                  {(walletBalance / 100).toFixed(0)}&euro;
                </button>
              )}
            </div>
          </div>
        )}
      </header>
    );
  }

  // Desktop header
  return (
    <header className="shop-sans bg-[#faf7f0] border-b-2 border-[#b45f2e]">
      <div className="px-4 lg:px-8 py-4">
        <div className="flex items-center gap-3 lg:gap-7">
          {/* Logo + Nom */}
          <button
            onClick={() => retourAccueil()}
            className="flex items-center gap-2.5 flex-none hover:opacity-85 transition-opacity"
            aria-label="Retour à l'accueil de la boutique"
          >
            <img
              src={logoSrc}
              alt={storeName}
              className="h-9 w-9 rounded-full object-contain"
            />
            <span
              className="shop-serif font-bold text-[19px] truncate max-w-[200px] lg:max-w-[280px] xl:max-w-none"
              style={{ color: TERRACOTTA }}
            >
              {storeName}
            </span>
          </button>

          {/* Barre de recherche */}
          <div className="flex-1 min-w-0 max-w-[520px]">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Rechercher un produit bio…"
                className="w-full pl-3.5 pr-12 py-2.5 text-[13px] bg-white border border-black/[.12] rounded-lg placeholder-[#6b6550] text-[#3a3324] focus:outline-none focus:border-[#b45f2e] focus:ring-2 focus:ring-[#b45f2e]/15 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-[#f0e9d8] hover:bg-[#e5dcc4] rounded-full transition-colors"
                  title="Effacer la recherche"
                >
                  <X className="w-4 h-4 text-[#4a4530]" />
                </button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="ml-auto flex items-center gap-3 lg:gap-5 whitespace-nowrap">
            {/* Lien Notre histoire */}
            <button
              onClick={() => navigate('/shop/philosophy')}
              className={`text-[13px] font-semibold hover:underline underline-offset-4 transition-all ${isAuthenticated ? 'hidden xl:block' : ''}`}
              style={{ color: OLIVE }}
              title="Notre philosophie"
            >
              Notre histoire
            </button>

            {/* Lien Actualités — le chiffre compte ce qui a été publié depuis la
                dernière ouverture du fil, et s'éteint dès qu'on l'ouvre. */}
            <button
              onClick={() => navigate('/shop/feed')}
              className={`relative text-[13px] font-semibold hover:underline underline-offset-4 transition-all ${isAuthenticated ? 'hidden xl:block' : ''}`}
              style={{ color: OLIVE }}
              title={actusNonLues > 0 ? `${actusNonLues} nouvelle${actusNonLues > 1 ? 's' : ''} actualité${actusNonLues > 1 ? 's' : ''}` : 'Voir les actualités'}
            >
              Actualités
              {actusNonLues > 0 && (
                <span
                  className="absolute -top-2 -right-3 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-[10px] font-bold px-1"
                  style={{ backgroundColor: TERRACOTTA }}
                >
                  {actusNonLues > 99 ? '99+' : actusNonLues}
                </span>
              )}
            </button>

            {/* Lien Abonnements (si connecté) */}
            {isAuthenticated && (
              <button
                onClick={() => navigate('/shop/subscriptions')}
                className="hidden xl:block text-[13px] font-semibold hover:underline underline-offset-4 transition-all"
                style={{ color: OLIVE }}
                title="Gérer mes abonnements"
              >
                Abonnements
              </button>
            )}

            {/* Wallet Badge */}
            {isAuthenticated && (
              <button
                onClick={() => navigate('/shop/wallet')}
                className="flex items-center gap-2 px-3 py-2 bg-[#f0e9d8] border border-[#4a5c3a]/25 rounded-lg hover:bg-[#e5dcc4] transition-colors"
                title="Mes FOLIES"
              >
                <span className="text-lg leading-none">💰</span>
                <span className="text-sm font-bold tabular-nums" style={{ color: OLIVE }}>
                  {(walletBalance / 100).toFixed(2)} &euro;
                </span>
              </button>
            )}

            {/* User Menu */}
            <div className="relative">
              {isAuthenticated ? (
                <>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-3.5 py-2 bg-white border border-black/[.12] rounded-lg hover:border-[#b45f2e]/50 transition-colors"
                  >
                    <span className="text-lg leading-none">👤</span>
                    <span className="text-[13px] font-semibold text-[#3a3324]">
                      {customer?.first_name}
                    </span>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-black/[.12] rounded-lg shadow-xl z-50">
                      <div className="p-2 space-y-1">
                        <button
                          onClick={() => {
                            navigate('/shop/profile');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-[#faf7f0] rounded-lg text-sm font-medium text-[#4a4530]"
                        >
                          👤 Mon profil
                        </button>
                        <button
                          onClick={() => {
                            navigate('/shop/orders');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-[#faf7f0] rounded-lg text-sm font-medium text-[#4a4530]"
                        >
                          📦 Mes commandes
                        </button>
                        <button
                          onClick={() => {
                            navigate('/shop/wallet');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-[#faf7f0] rounded-lg text-sm font-medium text-[#4a4530]"
                        >
                          💰 Mes FOLIES
                        </button>
                        <button
                          onClick={() => {
                            navigate('/shop/feed');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-[#faf7f0] rounded-lg text-sm font-medium text-[#4a4530]"
                        >
                          📢 Actualités
                          {actusNonLues > 0 && (
                            <span
                              className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-[10px] font-bold px-1"
                              style={{ backgroundColor: TERRACOTTA }}
                            >
                              {actusNonLues > 99 ? '99+' : actusNonLues}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            navigate('/shop/subscriptions');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-[#faf7f0] rounded-lg text-sm font-medium text-[#4a4530]"
                        >
                          🔔 Mes abonnements
                        </button>
                        <button
                          onClick={() => {
                            navigate('/shop/philosophy');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-[#faf7f0] rounded-lg text-sm font-medium text-[#4a4530]"
                        >
                          🌱 Notre histoire
                        </button>
                        <div className="border-t border-black/[.08] my-1"></div>
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 hover:bg-red-50 rounded-lg text-sm font-medium text-red-600"
                        >
                          🚪 Déconnexion
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={() => navigate('/shop/login')}
                  className="px-5 py-2.5 text-white text-[13px] font-bold rounded-lg transition-opacity hover:opacity-90"
                  style={{ backgroundColor: TERRACOTTA }}
                >
                  Connexion
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fermer le menu user si on clique ailleurs */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </header>
  );
}
